import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCreate, useGetIdentity } from "ra-core";
import { Link } from "react-router";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ExternalLink,
  Filter,
  MoreHorizontal,
  RefreshCw,
  Target,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../providers/supabase/supabase";

// Sprint 10.x — Carte "Inscrits à closer (signal PostHog)" pour Théo.
// Consomme l'edge function `revenue_top_accounts` qui croise PostHog
// (paywall_view, plan_created, login) + CRM (heat_tier, subscription_status)
// pour sortir les 15-20 leads les plus chauds à appeler aujourd'hui.
//
// Layout : header collapsible + filtre secteur multi-select + liste de rows
// avec pastille bucket, signaux compacts, recommended_action.
// Action principale : "Marquer relancé" → contact_notes + remove row local.

const ALL_SECTORS = ["ESI", "SSI", "CF", "BE"] as const;
type Sector = (typeof ALL_SECTORS)[number];

type Bucket =
  | "paywall_no_pay"
  | "engaged_no_paywall"
  | "active_no_engagement"
  | "cold_signup"
  | "unknown";

type HeatTier = "brulant" | "chaud" | "tiede" | "froid" | "mort" | null;

interface RevenueAccountSignals {
  paywall_count: number;
  plan_count: number;
  login_count: number;
  first_seen: string | null;
  last_seen: string | null;
  last_paywall_at: string | null;
  stuck_days: number | null;
}

interface RevenueAccount {
  email: string;
  contact_id: number | null;
  company_id: number | null;
  name: string | null;
  company_name: string | null;
  sector: Sector | null;
  heat_tier: HeatTier;
  heat_score: number | null;
  intent_score: number;
  bucket: Bucket;
  signals: RevenueAccountSignals;
  estimated_value_eur_monthly: number | null;
  reason: string | null;
  recommended_action: string | null;
  crm_synced: boolean;
}

interface RevenueTopAccountsResponse {
  generated_at: string;
  period_days: number;
  summary: {
    total_persons_seen: number;
    total_paywall_hitters_non_paying: number;
    total_signups_period: number;
    estimated_revenue_potential_eur: number;
  };
  accounts: RevenueAccount[];
}

const BUCKET_META: Record<
  Bucket,
  { emoji: string; label: string; noteAction: "phone" | "email" }
> = {
  paywall_no_pay: {
    emoji: "🔥",
    label: "Paywall sans paiement",
    noteAction: "phone",
  },
  engaged_no_paywall: {
    emoji: "🌶️",
    label: "Engagé sans paywall",
    noteAction: "email",
  },
  active_no_engagement: {
    emoji: "🟡",
    label: "Actif sans engagement",
    noteAction: "email",
  },
  cold_signup: { emoji: "❄️", label: "Inscrit froid", noteAction: "email" },
  unknown: { emoji: "⚪", label: "Inconnu", noteAction: "email" },
};

const COLLAPSE_KEY = "revenue-top-accounts-collapsed";
const SECTORS_KEY = "revenue-top-accounts-sectors";
const DEFAULT_LIMIT = 15;
const DEFAULT_DAYS = 30;

function readCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

function readSectors(): Sector[] {
  if (typeof window === "undefined") return [...ALL_SECTORS];
  try {
    const raw = localStorage.getItem(SECTORS_KEY);
    if (!raw) return [...ALL_SECTORS];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [...ALL_SECTORS];
    const filtered = parsed.filter((v): v is Sector =>
      ALL_SECTORS.includes(v as Sector),
    );
    return filtered.length > 0 ? filtered : [...ALL_SECTORS];
  } catch {
    return [...ALL_SECTORS];
  }
}

function intentBadgeClasses(score: number): string {
  if (score >= 80) return "bg-fabrik-red text-white border-fabrik-red";
  if (score >= 60)
    return "bg-fabrik-orange-soft text-fabrik-orange border-fabrik-orange/40";
  if (score >= 40)
    return "bg-fabrik-orange-soft/60 text-fabrik-orange/80 border-fabrik-orange/30";
  return "bg-fabrik-gray-pill text-fabrik-text-3 border-transparent";
}

function buildSignalsLine(signals: RevenueAccountSignals): string {
  const parts: string[] = [];
  if (signals.paywall_count > 0) {
    const pluralPw = signals.paywall_count > 1 ? "paywalls" : "paywall";
    const days = signals.stuck_days != null ? ` en ${signals.stuck_days}j` : "";
    parts.push(`${signals.paywall_count} ${pluralPw}${days}`);
  }
  if (signals.plan_count > 0) {
    parts.push(
      `${signals.plan_count} plan${signals.plan_count > 1 ? "s" : ""}`,
    );
  }
  if (signals.login_count > 0) {
    parts.push(
      `${signals.login_count} login${signals.login_count > 1 ? "s" : ""}`,
    );
  }
  return parts.join(" · ");
}

function displayName(account: RevenueAccount): string {
  if (account.name && account.name.trim()) return account.name;
  return account.email;
}

async function fetchRevenueTopAccounts(
  sectors: Sector[],
): Promise<RevenueTopAccountsResponse> {
  const body: { limit: number; days: number; sectors: Sector[] } = {
    limit: DEFAULT_LIMIT,
    days: DEFAULT_DAYS,
    sectors,
  };
  const { data, error } =
    await getSupabaseClient().functions.invoke<RevenueTopAccountsResponse>(
      "revenue_top_accounts",
      { body },
    );
  if (error) throw error;
  if (!data) throw new Error("empty_response");
  return data;
}

export function RevenueTopAccountsCard() {
  // `revenue_top_accounts` rend du CA et répond désormais 403 aux non-admins
  // (comme TreasuryBanner côté finance) : on masque la carte plutôt que de
  // laisser l'appel échouer.
  const { identity } = useGetIdentity();
  const isAdmin = !!(identity as unknown as { administrator?: boolean })
    ?.administrator;
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());
  const [sectors, setSectors] = useState<Sector[]>(() => readSectors());
  const [dismissedEmails, setDismissedEmails] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      // localStorage indisponible (incognito strict) — fail silently
    }
  }, [collapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(SECTORS_KEY, JSON.stringify(sectors));
    } catch {
      // ignore
    }
  }, [sectors]);

  const sortedSectors = useMemo(() => [...sectors].sort(), [sectors]);

  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["revenue_top_accounts", sortedSectors],
    queryFn: () => fetchRevenueTopAccounts(sortedSectors),
    staleTime: 60_000,
    enabled: isAdmin,
  });

  const accountsToShow = useMemo(
    () => (data?.accounts ?? []).filter((a) => !dismissedEmails.has(a.email)),
    [data?.accounts, dismissedEmails],
  );

  const handleToggleSector = (sector: Sector, checked: boolean) => {
    setSectors((prev) => {
      const next = checked
        ? Array.from(new Set([...prev, sector]))
        : prev.filter((s) => s !== sector);
      // Au minimum, on garde tous les secteurs si l'utilisateur décoche tout
      // (le backend retournerait sinon sans filtre — autant l'expliciter).
      return next.length === 0 ? [...ALL_SECTORS] : next;
    });
  };

  const handleDismiss = (email: string) => {
    setDismissedEmails((prev) => {
      const next = new Set(prev);
      next.add(email);
      return next;
    });
  };

  const handleRefresh = () => {
    setDismissedEmails(new Set());
    void refetch();
  };

  const summary = data?.summary;
  const totalHits = summary?.total_paywall_hitters_non_paying ?? 0;
  const potential = summary?.estimated_revenue_potential_eur ?? 0;

  if (!isAdmin) return null;

  return (
    <Card>
      <CardContent className="py-4 md:py-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? "Dérouler la carte" : "Replier la carte"}
            className="flex items-start gap-2 min-w-0 flex-1 text-left hover:opacity-90"
          >
            <Target className="size-4 text-fabrik-red shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-foreground flex items-center gap-1.5">
                Inscrits à closer (PostHog)
                <ChevronDown
                  className={cn(
                    "size-3.5 text-fabrik-text-3 transition-transform",
                    collapsed && "-rotate-90",
                  )}
                />
              </p>
              {summary ? (
                <p className="text-[11.5px] text-muted-foreground mt-0.5">
                  {totalHits} compte{totalHits > 1 ? "s" : ""} prêt
                  {totalHits > 1 ? "s" : ""} à payer · potentiel ~
                  <span className="font-medium text-foreground">
                    {potential}€/mois
                  </span>
                </p>
              ) : null}
            </div>
          </button>
          <div className="flex items-center gap-1.5 shrink-0">
            <SectorFilterDropdown
              sectors={sectors}
              onToggle={handleToggleSector}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 text-fabrik-text-3 hover:text-foreground"
              onClick={handleRefresh}
              disabled={isFetching}
              aria-label="Rafraîchir"
            >
              <RefreshCw
                className={cn("size-3.5", isFetching && "animate-spin")}
              />
            </Button>
          </div>
        </div>

        {!collapsed && (
          <>
            {isPending ? (
              <SkeletonRows />
            ) : isError ? (
              <ErrorBanner
                message={(error as Error)?.message ?? "erreur inconnue"}
                onRetry={handleRefresh}
              />
            ) : accountsToShow.length === 0 ? (
              <p className="text-[12.5px] text-muted-foreground py-2">
                Aucun signal d'achat actif. Tous tes leads payants ou à dormir
                💤
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-border/40">
                {accountsToShow.map((account) => (
                  <AccountRow
                    key={account.email}
                    account={account}
                    onDismiss={() => handleDismiss(account.email)}
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SectorFilterDropdown({
  sectors,
  onToggle,
}: {
  sectors: Sector[];
  onToggle: (sector: Sector, checked: boolean) => void;
}) {
  const allSelected = sectors.length === ALL_SECTORS.length;
  const label = allSelected ? "Tous" : sectors.join(", ");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 gap-1.5 text-[11.5px]"
          aria-label="Filtrer par secteur"
        >
          <Filter className="size-3" />
          <span className="hidden sm:inline truncate max-w-[80px]">
            {label}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-fabrik-text-3">
          Secteurs
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {ALL_SECTORS.map((sector) => (
          <DropdownMenuCheckboxItem
            key={sector}
            checked={sectors.includes(sector)}
            onCheckedChange={(checked) => onToggle(sector, !!checked)}
            onSelect={(e) => e.preventDefault()}
          >
            {sector}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SkeletonRows() {
  return (
    <div className="flex flex-col gap-2 py-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  );
}

function ErrorBanner({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-md border border-fabrik-red/40 bg-fabrik-red-soft px-3 py-2.5 flex items-start gap-2">
      <AlertTriangle className="size-4 text-fabrik-red shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-medium text-foreground">
          Échec récupération signaux PostHog
        </p>
        <p className="text-[11.5px] text-muted-foreground truncate">
          {message}
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-7 px-2 gap-1.5 text-xs shrink-0"
        onClick={onRetry}
      >
        <RefreshCw className="size-3" />
        Réessayer
      </Button>
    </div>
  );
}

function AccountRow({
  account,
  onDismiss,
}: {
  account: RevenueAccount;
  onDismiss: () => void;
}) {
  const { identity } = useGetIdentity();
  const [createNote] = useCreate();
  const [isMarking, setIsMarking] = useState(false);

  const bucketMeta = BUCKET_META[account.bucket] ?? BUCKET_META.unknown;
  const signalsLine = buildSignalsLine(account.signals);
  const name = displayName(account);

  const canLinkToContact = account.crm_synced && account.contact_id != null;
  const contactHref = canLinkToContact
    ? `/contacts/${account.contact_id}/show`
    : null;

  const handleMarkRelance = async () => {
    if (isMarking) return;
    if (account.contact_id == null) {
      toast.error("Contact non synchronisé dans le CRM", {
        description: "Crée d'abord la fiche depuis Inscrits récents.",
      });
      return;
    }
    setIsMarking(true);

    const noteText = `Relance commerciale ciblée après signal PostHog : ${bucketMeta.label}${
      account.recommended_action ? ` · ${account.recommended_action}` : ""
    }`;

    try {
      await new Promise<void>((resolve, reject) =>
        createNote(
          "contact_notes",
          {
            data: {
              contact_id: account.contact_id,
              text: noteText,
              date: new Date().toISOString(),
              sales_id: identity?.id,
            },
          },
          {
            onSuccess: () => resolve(),
            onError: (err) => reject(err),
          },
        ),
      );
      toast.success("Relance archivée dans la timeline");
      onDismiss();
    } catch (err) {
      const msg = (err as { message?: string }).message ?? "Erreur inconnue";
      toast.error("Impossible d'archiver la relance", { description: msg });
      setIsMarking(false);
    }
  };

  return (
    <li className="py-2.5 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="text-[16px] leading-none mt-0.5 shrink-0"
          title={bucketMeta.label}
        >
          {bucketMeta.emoji}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="min-w-0 flex items-center gap-1.5 flex-wrap">
              {contactHref ? (
                <Link
                  to={contactHref}
                  className="text-sm font-medium text-foreground hover:underline truncate"
                >
                  {name}
                </Link>
              ) : (
                <span className="text-sm font-medium text-foreground truncate">
                  {name}
                </span>
              )}
              {account.company_name && (
                <span className="text-xs text-muted-foreground truncate">
                  · {account.company_name}
                </span>
              )}
              {account.sector && (
                <Badge
                  variant="outline"
                  className="text-[10px] h-4 px-1.5 py-0 border-fabrik-border-strong text-fabrik-text-3"
                >
                  {account.sector}
                </Badge>
              )}
            </div>
            <Badge
              variant="outline"
              className={cn(
                "text-[10.5px] h-5 px-2 py-0 font-semibold tabular-nums shrink-0",
                intentBadgeClasses(account.intent_score),
              )}
              aria-label={`Intent score ${account.intent_score}`}
            >
              intent {account.intent_score}
            </Badge>
          </div>
          {signalsLine && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {signalsLine}
            </p>
          )}
          {account.recommended_action && (
            <p className="text-[12px] italic text-fabrik-text-3 mt-0.5 leading-snug">
              {account.recommended_action}
            </p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 text-fabrik-text-3 hover:text-foreground"
              aria-label="Plus d'actions"
              disabled={isMarking}
            >
              <MoreHorizontal className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {canLinkToContact && contactHref ? (
              <DropdownMenuItem asChild>
                <Link to={contactHref}>
                  <ExternalLink className="size-3.5" />
                  Voir la fiche
                </Link>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem disabled>
                <ExternalLink className="size-3.5" />
                Contact non synchronisé
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={handleMarkRelance}
              disabled={!canLinkToContact || isMarking}
            >
              <Check className="size-3.5" />
              Marquer relancé
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}
